// ** Hook Import
import { useSettings } from 'src/@core/hooks/useSettings'

// ** Util Import
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'

const GlobalStyles = theme => {
  // ** Hook & Var
  const { settings } = useSettings()
  const { mode } = settings

  const perfectScrollbarThumbBgColor = () => {
    if (mode === 'light') {
      return '#C2C4D1 !important'
    } else {
      return '#504B6D !important'
    }
  }

  /*
    Color del contorno de foco, por modo.

    No es una preferencia estetica: el minimo de contraste de WCAG para un
    indicador de foco es 3:1 contra lo que tiene al lado, y ningun color unico
    cumple en los dos modos. Medido contra los fondos reales del tema:

      #0278ae (azul de marca)  claro 4,49 / 4,88   oscuro 3,05 / 2,68  <- falla
      #38b6f0 (azul claro)     claro 2,12 / 2,30   oscuro 6,47 / 5,67  <- falla

    Por eso vive aca y no en globals.scss: el modo oscuro de este tema no deja
    ninguna marca en el DOM —ni atributo en <html> ni clase en <body>, comprobado—,
    asi que desde una hoja estatica no hay a que engancharse.
  */
  const colorDeFoco = mode === 'light' ? '#0278AE' : '#38B6F0'

  return {
    /*
      Indicador de foco de teclado.

      Medido sobre /troops/: de 12 elementos enfocables, 1 mostraba algo al recibir
      el foco. themeConfig tiene disableRipple en true y MUI cuelga su indicador del
      ripple, asi que los botones quedaban con la clase .Mui-focusVisible y nada
      visible; los enlaces del contenido nunca tuvieron, porque el reset del
      template quita el outline del navegador. Quien navega con teclado no sabia
      donde estaba parado en ninguna pantalla.

      :focus-visible y no :focus: el navegador solo lo aplica cuando el foco llego
      por teclado, asi que un clic con el raton no deja el contorno pegado.
    */
    [[
      'a:focus-visible',
      'button:focus-visible',
      '[role="button"]:focus-visible',
      'input:focus-visible',
      'select:focus-visible',
      'textarea:focus-visible',
      'summary:focus-visible',
      '[tabindex]:focus-visible',
      // MUI marca asi sus propios controles cuando el foco vino por teclado
      '.Mui-focusVisible'
    ].join(', ')]: {
      /*
        !important y selectores de verdad, no :where().

        La primera version usaba :where(...), que tiene especificidad cero: los
        estilos de emotion de cada componente de MUI le ganaban a todo. Medido, se
        cayo de 14 de 14 elementos con indicador a 1 de 14, y a 0 en modo oscuro.
      */
      outline: `2px solid ${colorDeFoco} !important`,
      outlineOffset: '2px !important',
      borderRadius: '2px'
    },

    'body[style^="padding-right"] .layout-navbar-and-nav-container::after': {
      content: '""',
      position: 'absolute',
      left: '100%',
      top: 0,
      height: '100%',
      backgroundColor: hexToRGBA(theme.palette.background.paper, 0.85),
      width: '30px'
    },
    '.demo-space-x > *': {
      marginTop: '1rem !important',
      marginRight: '1rem !important',
      'body[dir="rtl"] &': {
        marginRight: '0 !important',
        marginLeft: '1rem !important'
      }
    },
    '.demo-space-y > *:not(:last-of-type)': {
      marginBottom: '1rem'
    },
    '.MuiGrid-container.match-height .MuiCard-root': {
      height: '100%'
    },
    '.ps__rail-y': {
      zIndex: 1,
      right: '0 !important',
      left: 'auto !important',
      '&:hover, &:focus, &.ps--clicking': {
        backgroundColor: theme.palette.mode === 'light' ? '#E4E5EB !important' : '#423D5D !important'
      },
      '& .ps__thumb-y': {
        right: '3px !important',
        left: 'auto !important',
        backgroundColor: theme.palette.mode === 'light' ? '#C2C4D1 !important' : '#504B6D !important'
      },
      '.layout-vertical-nav &': {
        '& .ps__thumb-y': {
          width: 4,
          backgroundColor: perfectScrollbarThumbBgColor()
        },
        '&:hover, &:focus, &.ps--clicking': {
          backgroundColor: 'transparent !important',
          '& .ps__thumb-y': {
            width: 6
          }
        }
      }
    },
    '#nprogress': {
      pointerEvents: 'none',
      '& .bar': {
        left: 0,
        top: 0,
        height: 3,
        width: '100%',
        zIndex: 2000,
        position: 'fixed',
        backgroundColor: theme.palette.primary.main
      }
    }
  }
}

export default GlobalStyles
